import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import { Button, Conditional, Else, Heading, If, mappedArray, Row } from "@hex-insights/core";
import {
	HolidayFilterForm,
	HolidayFilterFormState,
	HolidayFilterFormUtils,
	HolidayFormat,
	HolidayIndexQuery,
	HolidayOrderField,
	HolidayOrderForm,
	HolidayOrderFormState,
	HolidayOrderFormValues,
	HolidayPaginationForm,
	useHolidayIndexQuery,
} from "@hex-insights/roadium.shared";
import { InternalLink, useActivePageRegistration } from "@hex-insights/router";
import { Table } from "@hex-insights/tables";
import { Main } from "../../../../Components";
import { holidayHubCreatePageInfo } from "../CreatePage/pageinfo";
import { holidayHubDetailPageInfo } from "../DetailPage/pageinfo";
import { holidayHubIndexPageInfo } from "./pageinfo";

const columns: (keyof HolidayIndexQuery["holidayConnection"]["edges"][0]["node"])[] = ["name", "startDate", "endDate"];

function orderFieldToColumn(field: HolidayOrderField | null) {
	if (field === null) {
		return "id";
	}
	return HolidayOrderFormValues.orderFieldToModelField[field];
}

function columnToOrderField(column: string) {
	if (column === "id") {
		return null;
	}
	return HolidayOrderFormValues.modelFieldToOrderField[column];
}

export function HolidayHubIndexPage() {
	useActivePageRegistration(holidayHubIndexPageInfo);

	const filterFormState = HolidayFilterFormState.useFilterFormStateWithQueryStateSync();
	const orderFormState = HolidayOrderFormState.useOrderFormStateWithQueryStateSync();
	const paginationFormState = IndexForms.usePaginationFormStateWithQueryStateAndLocalStorageSync();

	const filterInputs = IndexForms.useFilterInput(filterFormState.formValues, HolidayFilterFormUtils.toFilterInputs);
	const orderInput = IndexForms.useOrderInput(orderFormState.formValues);
	const queryVariables = IndexForms.useQueryVariables(
		filterInputs,
		orderInput,
		paginationFormState.formValues,
		paginationFormState.formSetFunctions.cursor,
	);
	const { loading, data, error } = useHolidayIndexQuery({ variables: queryVariables });

	const clearFilters = React.useCallback(() => {
		HolidayFilterFormUtils.clearFilters(filterFormState);
	}, [filterFormState]);

	const numActiveFilters = HolidayFilterFormUtils.countActiveFilters(filterFormState.formValues);
	const [paginationInfo, setPaginationInfo] = IndexForms.usePaginationInfo();

	const tableSortingProps = IndexForms.useTableSortingFromOrderForm(
		orderFormState,
		orderFieldToColumn,
		columnToOrderField,
	);

	const numFillerRows = Math.max(
		(paginationFormState.formValues.resultsPerPage ?? 0) - (data?.holidayConnection.edges.length ?? 0),
		0,
	);

	return (
		<Main>
			<Row justify="space-between" align="center" style={{ margin: "var(--heading--level-1---margin)" }}>
				<Heading level={1} noMargin>
					Holidays
				</Heading>

				<InternalLink to={holidayHubCreatePageInfo.to}>New Holiday</InternalLink>
			</Row>

			<Row
				justify="spaced-start"
				align="center"
				horizontalSpacing="0.25rem"
				withVerticalSpacing
				overflow="wrap"
				style={{ marginBottom: "0.75rem" }}
			>
				<HolidayFilterForm.Search formState={filterFormState} />
				<HolidayFilterForm.FilterController formState={filterFormState} numActiveFilters={numActiveFilters} />
				<If condition={numActiveFilters > 0}>
					<Button variant="link" size="small" onClick={clearFilters}>
						Clear
					</Button>
				</If>
				<HolidayOrderForm.OrderController formState={orderFormState} />
			</Row>

			<Table.Container className="hub__index-page__table__container">
				<Table
					columns={columns}
					minColumnWidth="10rem"
					scrollSyncID={paginationFormState.formValues.cursor}
					{...tableSortingProps}
					className="hub__index-page__table"
				>
					<Table.Header className="hub__index-page__table__header">
						<Table.Row>
							<Table.Heading column="name">Name</Table.Heading>
							<Table.Heading column="startDate">Start Date</Table.Heading>
							<Table.Heading column="endDate">End Date</Table.Heading>
						</Table.Row>
					</Table.Header>

					<Table.Body className="hub__index-page__table__body">
						<Conditional>
							<If condition={loading}>
								<Table.Row className="hub__index-page__table__row">
									<Table.Cell column="name" colspan={columns.length}>
										Loading...
									</Table.Cell>
								</Table.Row>
								{mappedArray((paginationFormState.formValues.resultsPerPage ?? 1) - 1, (i) => (
									<Table.FillerRow
										key={i}
										className="hub__index-page__table__row hub__index-page__table__row--filler"
									/>
								))}
							</If>

							<Else>
								{data?.holidayConnection.edges.map((e) => (
									<Table.Row key={e.node.id} className="hub__index-page__table__row">
										<Table.Cell column="name" oneLine>
											<InternalLink to={holidayHubDetailPageInfo.to(e.node.id)}>{e.node.name}</InternalLink>
										</Table.Cell>
										<Table.Cell column="startDate" oneLine>
											{HolidayFormat.Fields.startDateShort(e.node.startDate)}
										</Table.Cell>
										<Table.Cell column="endDate" oneLine>
											{e.node.isMultiDay ? HolidayFormat.Fields.endDateShort(e.node.endDate) : "-"}
										</Table.Cell>
									</Table.Row>
								))}
								{mappedArray(numFillerRows, (i) => (
									<Table.FillerRow
										key={i}
										className="hub__index-page__table__row hub__index-page__table__row--filler"
									/>
								))}
							</Else>
						</Conditional>
					</Table.Body>
				</Table>

				<Table.Toolbar>
					<Row justify="space-between" align="center">
						<HolidayPaginationForm.PaginationForm
							formState={paginationFormState}
							filters={filterInputs}
							order={orderInput}
							setPaginationInfo={setPaginationInfo}
						/>

						<span>
							<Conditional>
								<If condition={loading}>Loading...</If>
								<If condition={!!error}>Something went wrong.</If>
								<If condition={data?.holidayConnection.edges.length === 0}>No results found.</If>
								<Else>{IndexForms.getPaginationDescription(paginationInfo)}</Else>
							</Conditional>
						</span>
					</Row>
				</Table.Toolbar>
			</Table.Container>
		</Main>
	);
}
